import {captureAiVisionHandover} from './aiVisionCapture.js';
import {isLoading, modelRoot} from './state.js';

/**
 * Export of the neutral views and masks for the AI Vision Studio (#229807). Only active with
 * ?aivisionexport in the url of a product page: a button downloads the views and masks of the model
 * as shown (choose the size first) with the file names the studio expects in
 * /userdata/aivisionstudio/views/ (<item number>_<view>.jpg, <item number>_<view>_mask.png).
 */

const EXPORT_VIEWS = ['three_quarter', 'front', 'left', 'right', 'back', 'top'];

function getItemNo() {
    // product pages push the item into the data layer (view_item)
    const event = (window.dataLayer || []).find((entry) => entry?.event === 'view_item');
    return String(event?.ecommerce?.items?.[0]?.item_id || '').replace(/[^A-Za-z0-9-]/g, '');
}

function toJpeg(dataUrl) {
    return new Promise((resolve, reject) => {
        const image = new Image();
        image.onload = () => {
            const canvas = document.createElement('canvas');
            canvas.width = image.width;
            canvas.height = image.height;
            const context = canvas.getContext('2d');
            context.fillStyle = '#ffffff';
            context.fillRect(0, 0, canvas.width, canvas.height);
            context.drawImage(image, 0, 0);
            resolve(canvas.toDataURL('image/jpeg', 0.92));
        };
        image.onerror = reject;
        image.src = dataUrl;
    });
}

async function download(dataUrl, fileName) {
    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = fileName;
    document.body.append(link);
    link.click();
    link.remove();
    // browsers drop downloads that follow each other too quickly
    await new Promise((resolve) => setTimeout(resolve, 400));
}

export function initAiVisionExport() {
    if (!new URLSearchParams(window.location.search).has('aivisionexport')) {
        return;
    }
    const itemNo = getItemNo();
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'button aiVisionExportButton';
    button.textContent = `Ansichten und Masken exportieren (${itemNo || 'Artikelnummer fehlt'})`;
    button.disabled = !itemNo;
    Object.assign(button.style, {position: 'fixed', right: '16px', bottom: '16px', zIndex: '1000'});
    document.body.append(button);

    button.addEventListener('click', async () => {
        if (isLoading || !modelRoot) {
            button.textContent = 'Modell lädt noch – bitte erneut klicken';
            return;
        }
        button.disabled = true;
        const label = button.textContent;
        button.textContent = 'Export läuft …';
        try {
            const {views, masks} = await captureAiVisionHandover({scale: 2, withMasks: true, viewKeys: EXPORT_VIEWS});
            for (const [key, dataUrl] of Object.entries(views)) {
                await download(await toJpeg(dataUrl), `${itemNo}_${key}.jpg`);
            }
            for (const [key, dataUrl] of Object.entries(masks)) {
                await download(dataUrl, `${itemNo}_${key}_mask.png`);
            }
            button.textContent = `${Object.keys(views).length} Ansichten und ${Object.keys(masks).length} Masken exportiert`;
        } catch (error) {
            console.error('[AI VISION EXPORT] failed', error);
            button.textContent = 'Export fehlgeschlagen';
        }
        setTimeout(() => {
            button.textContent = label;
            button.disabled = false;
        }, 4000);
    });
}
